import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';
import type { Role, AssignablePermissions } from '../types/roles';

export type { Role, AssignablePermissions };
export type { RoleType, Permission } from '../types/roles';

interface RolesState {
  roles: Role[];
  assignablePermissions: AssignablePermissions | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: RolesState = {
  roles: [],
  assignablePermissions: null,
  isLoading: false,
  error: null,
};

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    setRoles(state, action: PayloadAction<Role[]>) {
      state.roles = action.payload;
    },
    addRole(state, action: PayloadAction<Role>) {
      state.roles.push(action.payload);
    },
    removeRole(state, action: PayloadAction<string>) {
      state.roles = state.roles.filter((r) => r.ID !== action.payload);
    },
    updateRole(state, action: PayloadAction<Role>) {
      const idx = state.roles.findIndex((r) => r.ID === action.payload.ID);
      if (idx !== -1) state.roles[idx] = action.payload;
    },
    setAssignablePermissions(state, action: PayloadAction<AssignablePermissions>) {
      state.assignablePermissions = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const { setRoles, addRole, removeRole, updateRole, setAssignablePermissions, setLoading, setError } =
  rolesSlice.actions;

export const fetchRoles =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.listRoles();
      dispatch(setRoles(response.data ?? []));
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to load roles'));
    } finally {
      dispatch(setLoading(false));
    }
  };

export const fetchAssignablePermissions =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const response = await apiService.getAssignablePermissions();
      dispatch(setAssignablePermissions(response.data));
    } catch {
      // Non-fatal: permission checklist simply won't be available
    }
  };

export default rolesSlice.reducer;
